import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import type { DeliveryDetail } from '@/types';
import { DeliveryStatusBadge, RecipientCell, SourceChips } from '@/components/ui';
import { TruncatedCell } from '@/components/table';
import { useToast } from '@/components/Toast';
import { formatTelegramError } from '@/utils/telegramErrors';
import { formatApiError } from '@/utils/formatApiError';
import { isUuid } from '@/utils/uuid';

type Props = {
  deliveryId: string;
  onClose: () => void;
  onRetry?: (delivery: DeliveryDetail) => Promise<void>;
  canManageJobs?: boolean;
  retrying?: boolean;
};

function formatTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

export default function DeliveryDetailDrawer({
  deliveryId,
  onClose,
  onRetry,
  canManageJobs = false,
  retrying = false,
}: Props) {
  const { toast } = useToast();
  const [detail, setDetail] = useState<DeliveryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!isUuid(deliveryId)) {
      setDetail(null);
      setError('Invalid delivery ID');
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError('');
    api
      .getDeliveryDetail(deliveryId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((e) => {
        if (!cancelled) setError(formatApiError(e, 'Failed to load delivery'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deliveryId]);

  const canRetry =
    detail && (detail.status === 'FAILED' || detail.status === 'SKIPPED') && Boolean(detail.subscription_id);

  return (
    <>
      <button type="button" className="job-drawer-backdrop" aria-label="Close delivery details" onClick={onClose} />
      <aside className="job-detail-drawer" aria-label="Delivery details">
        <header className="job-detail-drawer__header">
          <div>
            <h2>Delivery details</h2>
            {detail ? <DeliveryStatusBadge status={detail.status} /> : null}
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </header>

        {loading ? (
          <div className="loading-inline" role="status">
            <span className="spinner" aria-hidden />
            Loading…
          </div>
        ) : null}
        {error ? <div className="alert alert-error">{error}</div> : null}

        {detail ? (
          <div className="job-detail-drawer__meta">
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Delivery ID</span>
              <code>{detail.id}</code>
            </div>
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Sent</span>
              <span>{formatTime(detail.sent_at)}</span>
            </div>
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Created</span>
              <span>{formatTime(detail.created_at)}</span>
            </div>
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Search</span>
              <span>
                {detail.search_id && detail.search_name ? (
                  <Link to={`/searches/${detail.search_id}/links`} className="table-link">
                    {detail.search_name}
                  </Link>
                ) : (
                  detail.search_name || '—'
                )}
              </span>
            </div>
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Recipient</span>
              <RecipientCell
                recipient={{
                  display_name: detail.recipient_display_name,
                  telegram_username: detail.recipient_telegram_username,
                  chat_id: detail.chat_id,
                  telegram_resolution_method: detail.recipient_resolution_method,
                }}
              />
            </div>
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Channel</span>
              <span>{detail.channel}</span>
            </div>
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Chat ID</span>
              <code>{detail.chat_id || '—'}</code>
            </div>
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Source</span>
              {detail.source_name ? <SourceChips sources={[detail.source_name]} variant="compact" /> : '—'}
            </div>
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Subscription</span>
              <code>{detail.subscription_id || '—'}</code>
            </div>
            <div className="job-detail-drawer__meta-row">
              <span className="muted">Discovered link</span>
              <code>{detail.discovered_link_id}</code>
            </div>
            {detail.error_message ? (
              <div className="job-detail-drawer__meta-row">
                <span className="muted">Error</span>
                <span className="text-danger">{formatTelegramError(detail.error_message)}</span>
              </div>
            ) : null}
            {detail.job_id ? (
              <>
                <div className="job-detail-drawer__meta-row">
                  <span className="muted">Related job</span>
                  <Link to={`/jobs?job_id=${detail.job_id}`} className="table-link">
                    {detail.job_id.slice(0, 8)}… ({detail.job_status || 'unknown'})
                  </Link>
                </div>
                <div className="job-detail-drawer__meta-row">
                  <span className="muted">Job item</span>
                  <span>
                    {detail.job_item_id ? `${detail.job_item_id.slice(0, 8)}…` : '—'}
                    {detail.job_item_status ? ` · ${detail.job_item_status}` : ''}
                  </span>
                </div>
                <div className="job-detail-drawer__meta-row">
                  <span className="muted">Attempts</span>
                  <span>
                    {detail.attempt_count ?? 0}/{detail.max_attempts ?? '—'}
                  </span>
                </div>
                {detail.job_item_error ? (
                  <div className="job-detail-drawer__meta-row">
                    <span className="muted">Job error</span>
                    <span>{detail.job_item_error}</span>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}

        {detail?.message_preview ? (
          <section className="job-detail-drawer__section">
            <div className="job-detail-drawer__section-header">
              <h3>Message preview</h3>
            </div>
            <pre className="delivery-message-preview">{detail.message_preview}</pre>
          </section>
        ) : null}

        {detail?.url ? (
          <section className="job-detail-drawer__section">
            <div className="job-detail-drawer__section-header">
              <h3>Listing URL</h3>
            </div>
            <TruncatedCell value={detail.url} href={detail.url} copyable />
          </section>
        ) : null}

        {detail && canManageJobs ? (
          <div className="job-detail-drawer__actions">
            {canRetry && onRetry ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={retrying}
                onClick={() => {
                  void onRetry(detail).catch((e) => {
                    toast(formatApiError(e, 'Retry failed'), 'error');
                  });
                }}
              >
                {retrying ? 'Retrying…' : 'Retry delivery'}
              </button>
            ) : null}
            {detail.url ? (
              <a href={detail.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost">
                Open listing
              </a>
            ) : null}
          </div>
        ) : null}
      </aside>
    </>
  );
}
